import { computed, defineComponent } from 'vue'
import { RouterLink } from 'vue-router'
import { Col as VanCol, CountDown as VanCountDown, Row as VanRow } from 'vant'
import { parseNextDrawDatetime } from '@/components/lottery-tabs'
import {
  getBallAttributes,
  getZodiacYear,
  normalizeIssue,
  parseLotteryDate,
} from '@/utils/lottery-attr'
import { REGULAR_SLOTS } from '@/components/lottery-ball-row'
import type { BallColor } from '@/components/lottery-ball-row'
import type { LiveLotteryData } from '@/api/types.gen'

const BALL_COLOR_TO_AMAGE: Record<BallColor, 0 | 1 | 2 | 3> = {
  red: 1,
  blue: 2,
  green: 3,
  gray: 0,
}

function chineseColorToBallColor(c?: string): BallColor {
  if (c === '红') return 'red'
  if (c === '蓝') return 'blue'
  if (c === '绿') return 'green'
  return 'gray'
}

export function toNumberList(data: LiveLotteryData | null) {
  if (!data) return null
  const mains = data.main_numbers ?? []
  const wsColors = data.main_colors ?? []
  const wsZodiacs = data.main_zodiacs ?? []
  const wsElements = data.main_elements ?? []

  const drawDate = data.next_draw_date ?? ''
  const zodiacYear = drawDate ? getZodiacYear(parseLotteryDate(drawDate)) : null

  function build(num: string, idx: number, isSpecial: boolean) {
    const n = /^\d+$/.test(num) ? Number.parseInt(num, 10) : Number.NaN
    const hasValidN = !Number.isNaN(n) && n >= 1 && n <= 49

    let color: BallColor
    let zodiac: string
    let wuxing: string
    const wsColor = isSpecial ? data!.special_color : wsColors[idx]
    const wsZod = isSpecial ? data!.special_zodiac : wsZodiacs[idx]
    const wsEle = isSpecial ? data!.special_element : wsElements[idx]
    if (wsColor && wsZod && wsEle) {
      color = chineseColorToBallColor(wsColor)
      zodiac = wsZod
      wuxing = wsEle
    } else if (hasValidN && zodiacYear !== null) {
      const attrs = getBallAttributes(n, zodiacYear)
      color = attrs.color
      zodiac = attrs.zodiac
      wuxing = attrs.wuxing
    } else {
      color = 'gray'
      zodiac = ''
      wuxing = ''
    }
    return {
      number: num,
      color: BALL_COLOR_TO_AMAGE[color],
      shengXiao: zodiac,
      wuxing,
    }
  }

  const items = []
  for (let i = 0; i < 6; i++) items.push(build(mains[i] ?? '', i, false))
  items.push(build(data.special_number ?? '', 6, true))
  return items
}

/** 49208 borderHome 组件 —— 复刻 DOM，数据来自本项目 WS */
export const BorderHome = defineComponent({
  name: 'BorderHome',
  props: {
    data: { type: Object as () => LiveLotteryData | null, default: null },
    lotteryType: { type: String as () => 'hk' | 'macau', required: true },
    showHead: { type: Boolean, default: true },
    showFoot: { type: Boolean, default: true },
    /** 直接覆盖号码列表（对应 49208 `<border :data>`，跳过 WS 字段推导） */
    recordOverride: {
      type: Array as () => Array<{
        number: string
        color: 0 | 1 | 2 | 3
        shengXiao: string
        wuxing: string
      }> | null,
      default: null,
    },
    /** 覆盖 head 的期号文本(用于回放详情等不依赖 WS 的场景) */
    issueOverride: { type: String, default: '' },
    /** 隐藏 head 中间的"距下期开奖"倒计时 */
    hideCountdown: { type: Boolean, default: false },
    /** 覆盖 foot 文本(回放详情等不依赖 WS 的场景) */
    nextDrawOverride: { type: String, default: '' },
    /** 「查看历史记录」跳转路径（高手之家跳自己的历史页） */
    historyPath: { type: String, default: '/history' },
  },
  setup(props) {

    const countdownMs = computed(() => {
      if (!props.data) return 0
      const target = parseNextDrawDatetime(
        props.data.next_draw_date ?? '',
        props.data.next_draw_time ?? '',
      )
      if (!target) return 0
      return Math.max(0, target.getTime() - Date.now())
    })

    const nextDrawDisplay = computed(() => {
      const d = props.data
      if (!d) return ''
      return [
        `第${normalizeIssue(d.next_issue, d.next_draw_date)}期`,
        d.next_draw_date,
        d.next_draw_time,
        '星期' + d.next_draw_weekday?.replace(/^星期/, ''),
      ].join(' ')
    })

    // 非数字占位字符按索引顺序替换
    const PLACEHOLDER_CHARS = ['彩', '票', '助', '手', '开', '奖', '快']

    const replaceCharer = (str: string, index: number) => {
      if (/^\d+$/.test(str)) {
        return str
      }

      return PLACEHOLDER_CHARS[index] ?? str
    }

    return () => {
      const d = props.data
      const list = props.recordOverride ?? toNumberList(d)
      return (
        <div class="border-ws">
          <div class="border-box-body">
            {props.showHead && (
              <div class="border-box-body-hd">
                <VanRow style="display:flex;justify-content:space-between">
                  <VanCol span={7}>
                    <div class="text-left">
                      第<span>
                        {props.issueOverride ||
                          normalizeIssue(d?.current_issue, d?.next_draw_date) ||
                          '--'}
                      </span>期
                    </div>
                  </VanCol>
                  {!props.hideCountdown && (
                    <VanCol span={10}>
                      <div class="text-left lottys" style="display:flex">
                        距下期开奖：
                        <VanCountDown time={countdownMs.value} />
                      </div>
                    </VanCol>
                  )}
                  <VanCol span={7}>
                    <div class="text-right">
                      {/* `.text-right a` 与 `.text-right span` 同一套样式 */}
                      <RouterLink
                        class="btn-reset"
                        to={{ path: props.historyPath, query: { lotteryType: props.lotteryType } }}
                      >
                        <span>查看历史记录</span>
                      </RouterLink>
                    </div>
                  </VanCol>
                </VanRow>
              </div>
            )}
            <div class="border-box-body-bd">
              {list && (
                <div class="van-row">
                  {list.slice(0, 6).map((n, i) => (
                    <div key={REGULAR_SLOTS[i]} class="van-col tp left">
                      <div class={['icon-text', `bg-amage-${n.color}`]}>
                        <span class="slot-icon">
                          <span>{replaceCharer(n.number || '--', i)}</span>
                        </span>
                        {n.color !== 0 && (
                          <div class="slot-text">
                            {n.shengXiao}
                            {n.wuxing ? <span>/{n.wuxing}</span> : null}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  <div class="van-col tc">
                    <div class="connect">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="13"
                        height="12"
                        viewBox="0 0 13 12"
                        fill="none"
                      >
                        <path
                          d="M1.42856 6H11.4286"
                          stroke="#999"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M6.42856 1V11"
                          stroke="#999"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </div>
                  </div>
                  {list[6] && (
                    <div class="van-col tp right">
                      <div class={['icon-text', `bg-amage-${list[6].color}`]}>
                        <span class="slot-icon">
                          <span>{replaceCharer(list[6].number || '--', 6)}</span>
                        </span>
                        {list[6].color !== 0 && (
                          <div class="slot-text">
                            {list[6].shengXiao}
                            {list[6].wuxing ? <span>/{list[6].wuxing}</span> : null}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            {props.showFoot && (
              <div class="border-box-body-ft">
                {props.nextDrawOverride || nextDrawDisplay.value}
              </div>
            )}
          </div>
        </div>
      )
    }
  },
})

export default BorderHome
